import { pageMetadata } from 'shared/store/metadata'
import { ws } from 'shared/store/ws'
import { errors } from 'shared/utils'
import type { Dispatch } from 'redux'
import type { RootState } from './reducers'
import endpoints from 'shared/store/endpoints'

type GetState = () => RootState

export type ActionError = {
    type: 'ERROR',
    error: any
}

export type ActionAPIError = {
    type: 'PAGE_API_ERROR',
    page: string,
    error: any
}

export type ActionNetworkError = {
    type: 'PAGE_NETWORK_ERROR',
    networkError: any
}

export type ActionResetNetworkError = {
    type: 'RESET_NETWORK_ERROR'
}

export type ActionAPIAuthError = {
    type: 'PAGE_API_AUTH_ERROR',
    page: string,
    error: any
}

export type ActionUpdateToken = {
    type: 'UPDATE_TOKEN',
    token: string
}

const updateToken = (token: string): ActionUpdateToken => ({
    type: 'UPDATE_TOKEN',
    token
})

export const handleError = (
    error: string,
    dispatch: Dispatch<any>,
    page: string
) => {
    switch (error) {
        case errors.NETWORK:
            dispatch(
                actionNetworkError({
                    type: errors.NETWORK
                })
            )
            break
        case errors.TIMEOUT:
            dispatch(
                actionNetworkError({
                    type: errors.TIMEOUT
                })
            )
            break
        default:
            dispatch(actionAPIError(error, page))
            break
    }
}

const fetchPageData = (
    page: string,
    actionType: string,
    ignoreMeta?: boolean
) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        let retryCount = 0
        const doReq = async () => {
            try {
                const { token } = getState().userState
                const response = await ws.get(page, {
                    headers: token
                        ? { RenewToken: true, Authorization: token }
                        : {}
                })
                const { data, headers } = response
                if (data.metaData && !ignoreMeta) {
                    dispatch(pageMetadata(data.metaData))
                }
                if (headers && headers.authorization) {
                    dispatch(updateToken(headers.authorization))
                }
                dispatch({
                    type: actionType,
                    data: {
                        ...data,
                        error: false
                    }
                })
                return data
            } catch (error) {
                console.error('Error fetching page data', page, error)
                if (error.response) {
                    if (error.response.status === 401) {
                        dispatch(actionAuthAPIError(error, page))
                        return
                    }

                    if (
                        page === endpoints.kingdom.dashboard &&
                        error.response.status === 429
                    ) {
                        if (retryCount < 3) {
                            await new Promise(r =>
                                setTimeout(r, 1000 * Math.pow(2, retryCount))
                            )
                            retryCount++

                            return doReq()
                        }
                    }
                }

                handleError(error, dispatch, page)
                return error
            }
        }

        return doReq()
    }
}

const actionError = (error: any): ActionError => ({
    type: 'ERROR',
    error: error
})

const actionAPIError = (error: any, page: string): ActionAPIError => ({
    type: 'PAGE_API_ERROR',
    page: page,
    error: error
})

const actionNetworkError = (error: any): ActionNetworkError => ({
    type: 'PAGE_NETWORK_ERROR',
    networkError: error
})

const actionAuthAPIError = (error: any, page: string): ActionAPIAuthError => ({
    type: 'PAGE_API_AUTH_ERROR',
    page: page,
    error: error
})

export {
    fetchPageData,
    actionError,
    actionNetworkError,
    actionAPIError,
    updateToken
}

export type ActionsNetwork =
    | ActionError
    | ActionAPIError
    | ActionNetworkError
    | ActionResetNetworkError
    | ActionAPIAuthError
    | ActionUpdateToken
